import {
  Body,
  Button,
  Heading,
  Icon,
  Tooltip,
} from '@assembly-js/design-system';
import clsx from 'clsx';
import { FieldArray, useFormikContext } from 'formik';
import React from 'react';
import { v4 } from 'uuid';

import { DndWrapper } from 'src/components/DndWrapper';
import { LineItem } from 'src/routes/payments/components/lineItem-editor/lineItem';
import {
  LineItemField,
  LineItemFormSchemaExtended,
} from 'src/routes/payments/components/lineItem-editor/lineItem/Controller';
import { PresentationProps } from 'src/routes/payments/components/lineItem-editor/types';
import { AddProductRenderer } from 'src/routes/payments/invoices/components/AddProductRenderer';
import { ServiceSchema } from 'src/routes/payments/services/types';
import { SubscriptionInterval } from 'src/routes/payments/subscriptions/types/subscriptions';
import { LineItemType } from 'src/services/api/invoicesApi';
import { CreatePriceInput, Price, Product } from 'src/services/api/productsApi';

export enum LineItemEditorPricingMode {
  OneTime = 'OneTime',
  Recurring = 'Recurring',
  All = 'All',
  OneTimeStore = 'OneTimeStore',
  subscriptionsStore = 'subscriptionsStore',
}

type Props<Schema, AllowRecurringPrices extends boolean> = {
  // If true, the editor will ensure all line items have the same billing period
  allowRecurringPrices: AllowRecurringPrices;
  getLineItems: (o: Schema) => LineItemFormSchemaExtended[];
  // This should be something like "fields.lineItems" or "lineItems"
  formikLineItemFieldName: string;
  formikAmountFieldName: string;
  lineItemsInCents?: boolean;
  interval?: SubscriptionInterval;
  addProductLabel: string;
  mode: LineItemEditorPricingMode;
  onLineItemSelected?: (
    product: LineItemFormSchemaExtended,
    itemIndex: number,
  ) => void;
  onResetLineItem?: (itemIndex: number) => void;
  label: string;
  buttonLabel: string;
  tooltipDescription: string;
  allowCreatingNewProduct: boolean;
  allowCreatingNewPrice: boolean;
  allowAddNewOneTimeItem: boolean;
  renderPresentationComponent: (
    props: PresentationProps<AllowRecurringPrices>,
  ) => JSX.Element;
  isQuantityRequired?: boolean;
  maxLineItems?: number;
  showAllIntervalPrices?: boolean;
  restrictProductPriceReuse?: boolean;
  fieldName: LineItemField;
  showCreateNewProductItem: boolean;
  products: Product[];
  portalPrices: Price[];
  onAddServices?: (
    product: ServiceSchema,
  ) => Promise<{ data: Product } | { error: unknown }>;
  onAddServicePrice?: (
    price: CreatePriceInput,
  ) => Promise<{ data: Price } | { error: unknown }>;
  areAddServicePriceLoading?: boolean;
  isAddServiceLoading?: boolean;
};

export function LineItemEditor<Schema, AllowRecurringPrices extends boolean>({
  allowRecurringPrices,
  getLineItems,
  formikLineItemFieldName,
  formikAmountFieldName,
  addProductLabel,
  interval,
  mode,
  onLineItemSelected,
  onResetLineItem,
  label,
  buttonLabel,
  tooltipDescription,
  allowCreatingNewProduct: allowCreateNewProduct = true,
  allowCreatingNewPrice: allowCreateNewPrice = true,
  allowAddNewOneTimeItem = true,
  renderPresentationComponent,
  isQuantityRequired = true,
  maxLineItems,
  showAllIntervalPrices = false,
  restrictProductPriceReuse = false,
  fieldName,
  showCreateNewProductItem,
  products,
  portalPrices,
  onAddServices,
  onAddServicePrice,
  areAddServicePriceLoading,
  isAddServiceLoading,
}: Props<Schema, AllowRecurringPrices>) {
  const { values, setFieldValue } = useFormikContext<Schema>();
  const lineItems = getLineItems(values);
  const shouldAllowAddingLineItems = lineItems.every((x) => x.isValid);
  const isEditing = lineItems.some((x) => x.isEditing);
  const shouldAllowAddingLineItemsWithType = lineItems.every(
    (x) => x.type === LineItemType.Product,
  );
  const areAllLineItemsValid = lineItems.every((x) => x.isValid);

  const [draftLineItem, setdraftLineItem] =
    React.useState<LineItemFormSchemaExtended | null>(null);

  const isRecurringMode = mode === LineItemEditorPricingMode.Recurring;

  const showAllProducts = mode === LineItemEditorPricingMode.All;
  const isOneTimeStore = mode === LineItemEditorPricingMode.OneTimeStore;
  const isSubscriptionStore =
    mode === LineItemEditorPricingMode.subscriptionsStore;
  const maxAllowedItems = maxLineItems ?? Infinity;
  const canAddMoreItems = lineItems.length < maxAllowedItems;

  const pushFieldRef =
    React.useRef<
      (obj: {
        id: string;
        rate: string;
        description: string;
        quantity: string;
        total: number;
        type: LineItemType;
      }) => void
    >();

  const handleAddNewItem = () => {
    pushFieldRef.current?.({
      id: v4(),
      description: '',
      quantity: '1',
      rate: '',
      total: 0.0,
      type:
        isOneTimeStore || isSubscriptionStore
          ? LineItemType.Product
          : LineItemType.OneTime,
    });
  };

  const renderLineItem = (
    li: LineItemFormSchemaExtended,
    index: number,
    isDragging = false,
    remove: (index: number) => void,
  ) => (
    <LineItem
      key={li.id}
      itemIndex={index}
      onRemoveLineItem={remove}
      allowRecurringPrices={allowRecurringPrices}
      mode={mode}
      getLineItems={getLineItems}
      formikLineItemFieldName={formikLineItemFieldName}
      formikAmountFieldName={formikAmountFieldName}
      draftLineItem={draftLineItem}
      setdraftLineItem={setdraftLineItem}
      interval={interval}
      onLineItemSelected={onLineItemSelected}
      onResetLineItem={onResetLineItem}
      allowCreatingNewProduct={allowCreateNewProduct}
      allowCreatingNewPrice={allowCreateNewPrice}
      allowAddNewOneTimeItem={allowAddNewOneTimeItem}
      renderPresentationComponent={renderPresentationComponent}
      isQuantityRequired={isQuantityRequired}
      showAllIntervalPrices={showAllIntervalPrices}
      restrictProductPriceReuse={restrictProductPriceReuse}
      fieldName={fieldName}
      showCreateNewProductItem={showCreateNewProductItem}
      addProductRenderer={
        <AddProductRenderer
          label={addProductLabel}
          lineItem={li}
          fieldName={`${formikLineItemFieldName}.${index}`}
        />
      }
      isDragging={isDragging}
      products={products}
      portalPrices={portalPrices}
      onAddServices={onAddServices}
      onAddServicePrice={onAddServicePrice}
      areAddServicePriceLoading={areAddServicePriceLoading}
      isAddServiceLoading={isAddServiceLoading}
    />
  );

  return (
    <div>
      {!isOneTimeStore && !isSubscriptionStore && (
        <div className="py-6 flex items-center gap-2">
          <Heading className="text-xl font-medium text-dark-black">
            {label}
          </Heading>
          <Tooltip
            position="right"
            content={<Body className="text-[12px]">{tooltipDescription}</Body>}
          >
            <span>
              <Icon
                name="QuestionMark"
                icon="QuestionMark"
                className="h-3 w-3 text-gray-small"
              />
            </span>
          </Tooltip>
        </div>
      )}
      <FieldArray name={formikLineItemFieldName}>
        {({ push, remove }) => {
          pushFieldRef.current = push;
          return (
            <>
              <div
                className={clsx('flex flex-col gap-3 ', {
                  'border border-non-hover-border border-solid !gap-0':
                    (isOneTimeStore || isSubscriptionStore) &&
                    !isEditing &&
                    (areAllLineItemsValid || lineItems.length > 1),
                })}
              >
                {mode === LineItemEditorPricingMode.OneTimeStore ||
                mode === LineItemEditorPricingMode.subscriptionsStore ? (
                  <DndWrapper
                    hideDragIconOnMobile
                    items={lineItems}
                    onReorder={(newItems, oldItems) => {
                      const idToOriginal = new Map(
                        oldItems.map((item) => [item.id, item]),
                      );
                      const reordered = newItems
                        .map((mapped) => idToOriginal.get(mapped.id))
                        .filter((item) => item !== undefined);
                      setFieldValue(formikLineItemFieldName, reordered);
                    }}
                    draggableKey="line-item"
                    renderItem={(li, index, isDragging) =>
                      renderLineItem(li, index, isDragging, remove)
                    }
                    renderOverlay={(li, index) =>
                      renderLineItem(li, index, false, remove)
                    }
                    shouldBeDragItem={
                      shouldAllowAddingLineItems &&
                      lineItems.length > 1 &&
                      !isEditing
                    }
                    overLayBorder
                  />
                ) : (
                  lineItems.map((li, index) => (
                    <LineItem
                      key={li.id}
                      itemIndex={index}
                      onRemoveLineItem={remove}
                      allowRecurringPrices={allowRecurringPrices}
                      mode={mode}
                      getLineItems={getLineItems}
                      formikLineItemFieldName={formikLineItemFieldName}
                      formikAmountFieldName={formikAmountFieldName}
                      draftLineItem={draftLineItem}
                      setdraftLineItem={setdraftLineItem}
                      interval={interval}
                      onLineItemSelected={onLineItemSelected}
                      onResetLineItem={onResetLineItem}
                      allowCreatingNewProduct={allowCreateNewProduct}
                      allowCreatingNewPrice={allowCreateNewPrice}
                      allowAddNewOneTimeItem={allowAddNewOneTimeItem}
                      renderPresentationComponent={renderPresentationComponent}
                      isQuantityRequired={isQuantityRequired}
                      showAllIntervalPrices={showAllIntervalPrices}
                      restrictProductPriceReuse={restrictProductPriceReuse}
                      fieldName={fieldName}
                      showCreateNewProductItem={showCreateNewProductItem}
                      addProductRenderer={
                        <AddProductRenderer
                          label={addProductLabel}
                          lineItem={li}
                          fieldName={`${formikLineItemFieldName}.${index}`}
                        />
                      }
                      products={products}
                      portalPrices={portalPrices}
                      onAddServices={onAddServices}
                      onAddServicePrice={onAddServicePrice}
                      areAddServicePriceLoading={areAddServicePriceLoading}
                      isAddServiceLoading={isAddServiceLoading}
                    />
                  ))
                )}
              </div>
              {((!showAllProducts && shouldAllowAddingLineItems) ||
                (shouldAllowAddingLineItemsWithType && isRecurringMode)) &&
                !(isEditing && (isOneTimeStore || isSubscriptionStore)) && (
                  <div className="flex">
                    <Tooltip
                      position="right"
                      tooltipClassname="p-4"
                      disabled={canAddMoreItems}
                      content={
                        <Body className="text-sm text-black-heading">
                          Select up to {maxAllowedItems}
                          {'\u00A0'}
                          {isSubscriptionStore ? 'subscription' : 'one-time'}
                          {'\u00A0'}services
                        </Body>
                      }
                    >
                      <span>
                        <Button
                          label={buttonLabel}
                          onClick={handleAddNewItem}
                          prefixIcon="Plus"
                          variant="secondary"
                          className={clsx(
                            'text-base text-black-heading my-3 border-0 hover:bg-transparent p-0',
                            {
                              'text-light-gray': !canAddMoreItems,
                              'text-black-heading': canAddMoreItems,
                            },
                          )}
                          disabled={!canAddMoreItems}
                        />
                      </span>
                    </Tooltip>
                  </div>
                )}
            </>
          );
        }}
      </FieldArray>
    </div>
  );
}
